import { Injectable } from '@angular/core';
import get from 'lodash.get';
import { Product as BuyAgainProduct } from '~app/modules/account/modules/buy-again/models';
import { Attribute, Offer, Product } from '~app/modules/product/models';
import { BestOffer, Product as ProductModel } from '~shared/models';
import { ReferencePriceTypes } from '~shared/models/reference-price-types.enum';
import { IOfferLine } from '~shared/modules/cart/models';
import { CartOfferLine } from '~shared/modules/shopping-cart-store';
import { GaTrackingStringConstantEnum } from '../constants';
import { GaTrackingVasData } from '../models/ga-tracking-vas-data.model';
import { Category } from '~app/modules/generic/models';
import { NUMBER_0 } from '~shared/constants';
import { UrlUtils } from '~app/modules/generic/utils/url.utils';
import { PriceTypes } from '~shared/modules/price-type-toggler/models/price-types.enum';
import { getBlackWeekPaylodPropertiesGA4 } from '~shared/modules/ga-tracking/utils/ga-tracking-black-week.util';

type Offers = Offer | BestOffer | CartOfferLine | IOfferLine;
type Products = Product | ProductModel | BuyAgainProduct;

@Injectable({
  providedIn: 'root',
})
export class GaTrackingHelpersService {
  public static getReferencePriceTypeFromProduct(product: Products): ReferencePriceTypes | null {
    return get(product, 'bestOffer.referencePrice.priceType', null) || null;
  }

  public static getReferencePriceTypeFromOffer(offer: Offers): ReferencePriceTypes | null {
    return get(offer, 'referencePrice.priceType', null) || null;
  }

  public static getDimension31FromProduct(product: Products): number {
    return get(product, 'bestOffer.referencePrice.discountPercentage', 0);
  }

  public static getDimension43FromProduct(product: Attribute[]): string {
    const attributesCoverage = product.find((item) => item.code === 'custom_item_attributes_in_total');
    return attributesCoverage ? attributesCoverage.value.toString() : '0';
  }

  public static getVasRelatedDataFromOffer(offer: CartOfferLine): GaTrackingVasData {
    const dimension58 = offer.valueAddedServices?.length
      ? GaTrackingStringConstantEnum.Yes
      : GaTrackingStringConstantEnum.No;
    const service_price = (offer.valueAddedServices ?? []).reduce(
      (previousValue, currentValue) => previousValue + parseFloat(currentValue.price.unitPriceNet),
      0
    );

    return {
      dimension58,
      service_price,
    };
  }

  public static getExpressionResult(condition: boolean) {
    return condition ? GaTrackingStringConstantEnum.Yes : GaTrackingStringConstantEnum.No;
  }

  public static getProductCategoryTree(categoryList: Category[]) {
    return categoryList.map((category: Category) => ({
      id: category?.id,
      name: category?.name,
    }));
  }

  public static getCategoriesFromBreadcrumbsFull(category: Category): any {
    if (category && category.parents) {
      const categories = [...category.parents].reverse();
      categories.push({
        id: category.id,
        name: category.name,
        slug: category.slug,
      });
      return categories;
    } else {
      return [];
    }
  }

  public static getCategoryKeys(productCategories: Category[]) {
    return productCategories.reduce((categories: {}, category: Category, categoryIndex: number) => {
      if (categoryIndex === 0) {
        categories['item_category'] = category.name;
      } else {
        categories[`item_category${categoryIndex + 1}`] = category.name;
      }

      return categories;
    }, {});
  }

  public static getPrice(price, priceType: PriceTypes): number {
    return priceType === PriceTypes.b2c
      ? price?.vatIncluded?.amount || price?.gross || price?.grossPrice || `${NUMBER_0}`
      : price?.vatExcluded?.amount || price?.net || price?.netPrice || `${NUMBER_0}`;
  }

  public static getItemListName(previousUrl?: string) {
    if (UrlUtils.isSearchPage(previousUrl)) {
      return 'search';
    } else if (UrlUtils.isCatalogPage(previousUrl)) {
      return 'catalog';
    } else if (UrlUtils.isCustomPLPPage(previousUrl)) {
      return 'landing';
    } else if (UrlUtils.isCategoryPage(previousUrl)) {
      return 'category';
    } else if (UrlUtils.isBrandPlpPage(previousUrl)) {
      return 'brand';
    } else if (UrlUtils.isBuyAgainPage(previousUrl)) {
      return 'Buy Again';
    } else if (UrlUtils.isYourAssortmentPage(previousUrl)) {
      return 'Your Assortment';
    }
  }

  public static getServicePrice(itemHasVas: boolean, offer: Offer, priceType: PriceTypes, previousUrl: string) {
    return UrlUtils.isBuyAgainPage(previousUrl)
      ? undefined
      : !itemHasVas
      ? NUMBER_0
      : GaTrackingHelpersService.getPrice(offer?.valueAddedServices[0]?.price, priceType);
  }

  public static getServiceAddition(itemHasVas: boolean, previousUrl: string) {
    return UrlUtils.isBuyAgainPage(previousUrl)
      ? undefined
      : itemHasVas
      ? GaTrackingStringConstantEnum.Yes
      : GaTrackingStringConstantEnum.No;
  }

  public static getCustomItemforBuyAgain(product: Product): null | string {
    if (product instanceof BuyAgainProduct && product.notAvailableReason) {
      return `ba_nar=${product.notAvailableReason}`;
    }
    return null;
  }

  public static getItemObjectForGA4(
    product: Product,
    offer: Offer,
    priceType: PriceTypes,
    itemHasVas: boolean,
    categoryList: Category[],
    recommendation: boolean,
    quantity: number,
    EECLabel?: string,
    previousUrl?: string,
    isBlackDealsEnabled?: boolean
  ) {
    return {
      item_name: get(product, 'name', ''),
      item_id: get(product, 'id', ''),
      price: offer?.price ? GaTrackingHelpersService.getPrice(offer.price, priceType) : null,
      item_brand: get(product, 'brand.name', ''),
      ...GaTrackingHelpersService.getCategoryKeys(categoryList),
      item_list_name: GaTrackingHelpersService.getItemListName(previousUrl),
      item_promo: GaTrackingHelpersService.getExpressionResult(product?.hasPromotion),
      item_strikethrough: GaTrackingHelpersService.getReferencePriceTypeFromOffer(offer),
      item_energy_label: EECLabel,
      item_volume_pricing: GaTrackingHelpersService.getExpressionResult(!!offer?.volumePrice?.length),
      item_seller: get(offer, 'organization.shopName', ''),
      item_other_offers: GaTrackingHelpersService.getExpressionResult(product?.offers?.length > 1),
      item_offer_type: offer?.offerType,
      item_discount_rate: offer?.referencePrice?.discountPercentage,
      item_recommendation: GaTrackingHelpersService.getExpressionResult(recommendation),
      item_shipping_cost: GaTrackingHelpersService.getPrice(offer?.shippingGroup?.shippingCost?.unitCost, priceType),
      item_service_addition: GaTrackingHelpersService.getServiceAddition(itemHasVas, previousUrl),
      category_tree: GaTrackingHelpersService.getProductCategoryTree(categoryList),
      servicePrice: GaTrackingHelpersService.getServicePrice(itemHasVas, offer, priceType, previousUrl),
      item_offer_id: product?.bestOffer?.id,
      ...getBlackWeekPaylodPropertiesGA4(isBlackDealsEnabled, product),
      quantity,
      item_custom: GaTrackingHelpersService.getCustomItemforBuyAgain(product),
    };
  }
}
